import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { Button, Card, Field, FormAction, FormGrid } from '@crave/ui';
import { ApiError } from '@crave/api-client';
import { api } from '../api.ts';
import { Notice } from '../components/ui.tsx';

/** Narrowest credential column: two side by side on desktop, one column on a phone. */
const FORM_COL = 340;

interface Integration {
  provider: string;
  status: 'not_configured' | 'connected' | 'error' | 'disabled';
  mode: 'test' | 'live' | null;
  config: Record<string, unknown>;
  secrets: { key_name: string; hint: string | null; set_at: string }[];
  last_verified_at: string | null;
  last_error: string | null;
}
const str = (v: unknown): string => (typeof v === 'string' ? v : '');

interface IntegrationsResponse {
  items: Integration[];
  secrets_configured: boolean;
  api_public_url: string | null;
}

const isMfaError = (e: unknown): boolean => e instanceof ApiError && e.code === 'MFA_REQUIRED';
const errorText = (e: unknown, fallback: string): string =>
  e instanceof ApiError ? e.message : fallback;

/** Everything that shows whether payments or email are connected. */
function useRefreshConnections() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['integrations'] });
    void qc.invalidateQueries({ queryKey: ['admin-locations'] });
    void qc.invalidateQueries({ queryKey: ['readiness'] });
    void qc.invalidateQueries({ queryKey: ['dashboard'] });
  };
}

function MfaNotice({ action }: { action: string }) {
  return (
    <Notice kind="error">
      Two-factor sign-in is required to {action}.{' '}
      <Link to="/settings/security">Verify two-factor sign-in</Link>, then come back to this page.
    </Notice>
  );
}

function ErrorLine({ error, fallback }: { error: unknown; fallback: string }) {
  if (!error) return null;
  if (isMfaError(error)) return <MfaNotice action="change payment and email settings" />;
  return <Notice kind="error">{errorText(error, fallback)}</Notice>;
}

export function Integrations() {
  const q = useQuery({
    queryKey: ['integrations'],
    queryFn: async () => (await api.get<IntegrationsResponse>('/admin/integrations')).data,
    retry: (count, err) => !(err instanceof ApiError && err.status < 500) && count < 1,
  });

  // A failed background refetch keeps the last data. Keep the cards mounted so a key being typed
  // is not lost, and show the error above them.
  const loadError = !q.isError ? null : isMfaError(q.error) ? (
    <MfaNotice action="view or change payment and email settings" />
  ) : (
    <Notice kind="error">
      {q.error instanceof ApiError
        ? `Could not ${q.data ? 'refresh' : 'load'} integrations: ${q.error.message}`
        : 'Cannot reach the API.'}
    </Notice>
  );

  return (
    <section style={{ display: 'grid', gap: 16 }}>
      <h2 style={{ margin: 0 }}>Integrations</h2>
      {loadError}
      {q.data ? (
        <IntegrationList data={q.data} />
      ) : (
        !q.isError && <p style={{ margin: 0 }}>Loading…</p>
      )}
    </section>
  );
}

function IntegrationList({ data }: { data: IntegrationsResponse }) {
  const stripe = data.items.find((i) => i.provider === 'stripe');
  const resend = data.items.find((i) => i.provider === 'resend');
  const fooddata = data.items.find((i) => i.provider === FOODDATA);
  const twilio = data.items.find((i) => i.provider === 'twilio');
  const others = data.items.filter(
    (i) => !['stripe', 'resend', 'twilio', FOODDATA].includes(i.provider),
  );
  const canAutoWebhook = Boolean(data.api_public_url?.startsWith('https://'));
  return (
    <>
      {!data.secrets_configured && (
        <Card>
          <strong>Secrets are not enabled on the API.</strong> Set <code>SECRETS_MASTER_KEY</code>{' '}
          on the API (a Worker secret) before saving credentials.
        </Card>
      )}
      <StripeCard integration={stripe} canAutoWebhook={canAutoWebhook} />
      <ResendCard integration={resend} />
      <TwilioCard integration={twilio} />
      <FoodDataCard integration={fooddata} />
      {others.map((i) => (
        <Card key={i.provider}>
          <h3 style={{ marginTop: 0, textTransform: 'capitalize' }}>{i.provider}</h3>
          <p style={{ margin: 0, color: 'var(--ink-soft)' }}>
            {i.status === 'connected' ? 'Connected.' : 'Not configured.'}
          </p>
        </Card>
      ))}
    </>
  );
}

/* ---------------------------------- Stripe --------------------------------- */

interface StripeErrors {
  secret_key?: string;
  publishable_key?: string;
  webhook_secret?: string;
}

function validateStripe(secretKey: string, publishableKey: string, webhookSecret: string) {
  const errors: StripeErrors = {};
  const sk = secretKey.trim();
  const pk = publishableKey.trim();
  const wh = webhookSecret.trim();
  const mode = /^(sk|rk)_live_/.test(sk) ? 'live' : 'test';
  if (!/^(sk|rk)_(test|live)_[A-Za-z0-9]+$/.test(sk))
    errors.secret_key = 'Paste a secret key that starts with sk_test_ or sk_live_.';
  if (pk && !pk.startsWith(`pk_${mode}_`))
    errors.publishable_key = `Use a pk_${mode}_ key so it matches the secret key.`;
  if (wh && !wh.startsWith('whsec_'))
    errors.webhook_secret = 'Paste a signing secret that starts with whsec_.';
  return errors;
}

function StripeCard({
  integration: stripe,
  canAutoWebhook,
}: {
  integration: Integration | undefined;
  canAutoWebhook: boolean;
}) {
  const refresh = useRefreshConnections();
  const [secretKey, setSecretKey] = useState('');
  const [publishableKey, setPublishableKey] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [errors, setErrors] = useState<StripeErrors>({});
  const [saved, setSaved] = useState<string | null>(null);

  const connect = useMutation({
    mutationFn: async () =>
      (
        await api.put<{ status: string; mode: string; webhook: string }>(
          '/admin/integrations/stripe',
          {
            secret_key: secretKey.trim(),
            publishable_key: publishableKey.trim() || undefined,
            webhook_secret: webhookSecret.trim() || undefined,
            register_webhook: canAutoWebhook && !webhookSecret.trim(),
          },
        )
      ).data,
    onMutate: () => setSaved(null),
    onSuccess: (r) => {
      setSecretKey('');
      setPublishableKey('');
      setWebhookSecret('');
      setErrors({});
      setSaved(
        r.webhook === 'registered'
          ? 'Stripe connected. Crave registered its webhook endpoint.'
          : 'Stripe connected.',
      );
      refresh();
    },
    onError: refresh,
  });
  const disconnect = useMutation({
    mutationFn: async () => (await api.delete('/admin/integrations/stripe')).data,
    onMutate: () => setSaved(null),
    onSuccess: () => {
      setSaved('Stripe disconnected.');
      refresh();
    },
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    const next = validateStripe(secretKey, publishableKey, webhookSecret);
    setErrors(next);
    setSaved(null);
    connect.reset();
    if (Object.keys(next).length > 0) return;
    connect.mutate();
  }

  const form = (
    <StripeForm
      secretKey={secretKey}
      setSecretKey={setSecretKey}
      publishableKey={publishableKey}
      setPublishableKey={setPublishableKey}
      webhookSecret={webhookSecret}
      setWebhookSecret={setWebhookSecret}
      canAutoWebhook={canAutoWebhook}
      errors={errors}
      pending={connect.isPending}
      onSubmit={submit}
    />
  );

  return (
    <Card>
      <h3 style={{ marginTop: 0 }}>Stripe</h3>
      <div style={{ display: 'grid', gap: 8 }}>
        {saved && <Notice kind="ok">{saved}</Notice>}
        <ErrorLine error={connect.error} fallback="Could not connect Stripe." />
        <ErrorLine error={disconnect.error} fallback="Could not disconnect Stripe." />
      </div>
      {stripe?.status === 'connected' ? (
        <div style={{ display: 'grid', gap: 8, marginTop: 8 }}>
          <p style={{ margin: 0 }}>
            <strong style={{ color: 'var(--ok)' }}>Connected</strong> ·{' '}
            {stripe.mode === 'live' ? 'Live mode' : 'Test mode'} · account{' '}
            {str(stripe.config['account_id'])}
            {str(stripe.config['business_name']) ? ` (${str(stripe.config['business_name'])})` : ''}
          </p>
          <ul style={{ margin: 0 }}>
            {stripe.secrets.map((s) => (
              <li key={s.key_name}>
                {s.key_name === 'secret_key'
                  ? 'Secret key'
                  : s.key_name === 'publishable_key'
                    ? 'Publishable key'
                    : 'Webhook signing secret'}
                : <code>{s.hint}</code> · set {new Date(s.set_at).toLocaleString()}
              </li>
            ))}
            {!stripe.secrets.some((s) => s.key_name === 'webhook_secret') && (
              <li style={{ color: 'var(--warn)' }}>
                No webhook signing secret yet, so ordering cannot open.{' '}
                {canAutoWebhook
                  ? 'Open Replace the key and connect again with the signing secret left blank. Crave sets it up.'
                  : 'Open Replace the key and paste a signing secret.'}
              </li>
            )}
          </ul>
          <div>
            <Button
              variant="danger"
              onClick={() => {
                if (confirm('Disconnect Stripe? Online checkout stops until you connect it again.'))
                  disconnect.mutate();
              }}
              disabled={disconnect.isPending}
            >
              Disconnect Stripe
            </Button>
          </div>
          <details>
            <summary style={{ cursor: 'pointer' }}>Replace the key</summary>
            {form}
          </details>
        </div>
      ) : (
        <>
          {stripe?.status === 'error' && stripe.last_error && !connect.error && (
            <p style={{ color: 'var(--crave-red)' }}>Last attempt failed: {stripe.last_error}</p>
          )}
          <p style={{ color: 'var(--ink-soft)' }}>
            Paste the secret key from your Stripe dashboard. Crave verifies it with Stripe, stores
            it encrypted, and{' '}
            {canAutoWebhook
              ? 'registers its own webhook endpoint automatically.'
              : 'needs a webhook signing secret pasted here, because the API has no public https address.'}
          </p>
          {form}
        </>
      )}
    </Card>
  );
}

interface StripeFormProps {
  secretKey: string;
  setSecretKey: (v: string) => void;
  publishableKey: string;
  setPublishableKey: (v: string) => void;
  webhookSecret: string;
  setWebhookSecret: (v: string) => void;
  canAutoWebhook: boolean;
  errors: StripeErrors;
  pending: boolean;
  onSubmit: (e: FormEvent) => void;
}

function StripeForm(p: StripeFormProps) {
  return (
    <form onSubmit={p.onSubmit} style={{ display: 'grid', gap: 4, marginTop: 12 }}>
      <FormGrid min={FORM_COL}>
        <Field
          label="Secret key"
          placeholder="sk_test_… or sk_live_…"
          value={p.secretKey}
          onChange={(e) => p.setSecretKey(e.target.value)}
          autoComplete="off"
          spellCheck={false}
          error={p.errors.secret_key}
          required
          wide
        />
        <Field
          label="Publishable key (optional)"
          placeholder="pk_test_…"
          value={p.publishableKey}
          onChange={(e) => p.setPublishableKey(e.target.value)}
          autoComplete="off"
          spellCheck={false}
          hint="Used by the ordering site's payment form."
          error={p.errors.publishable_key}
        />
        <Field
          label={
            p.canAutoWebhook
              ? 'Webhook signing secret (optional override)'
              : 'Webhook signing secret'
          }
          placeholder="whsec_…"
          value={p.webhookSecret}
          onChange={(e) => p.setWebhookSecret(e.target.value)}
          autoComplete="off"
          spellCheck={false}
          hint={
            p.canAutoWebhook
              ? 'Leave blank and Crave creates the endpoint for you.'
              : 'From `stripe listen` or an endpoint you created in Stripe.'
          }
          error={p.errors.webhook_secret}
        />
      </FormGrid>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <Button type="submit" disabled={p.pending || !p.secretKey.trim()}>
          {p.pending ? 'Verifying with Stripe…' : 'Connect Stripe'}
        </Button>
      </div>
    </form>
  );
}

/* ------------------------------ USDA food data ----------------------------- */

/** integration_settings.provider for the USDA key used by barcode scans (#41). */
const FOODDATA = 'fooddata_central';
/** A free api.data.gov key: 40 letters and digits. */
const USDA_KEY_SHAPE = /^[A-Za-z0-9]{30,64}$/;

function FoodDataCard({ integration }: { integration: Integration | undefined }) {
  const refresh = useRefreshConnections();
  const [apiKey, setApiKey] = useState('');
  const [error, setError] = useState<string | undefined>();
  const [saved, setSaved] = useState<string | null>(null);
  const key = integration?.secrets.find((s) => s.key_name === 'api_key');
  const rejected = integration?.status === 'error';

  const save = useMutation({
    mutationFn: async (k: string) =>
      (await api.put<{ status: string }>('/admin/integrations/fooddata', { api_key: k })).data,
    onMutate: () => setSaved(null),
    onSuccess: () => {
      setApiKey('');
      setError(undefined);
      setSaved('USDA key saved. Barcode scans now check USDA labels first, then Open Food Facts.');
      refresh();
    },
  });
  const remove = useMutation({
    mutationFn: async () => (await api.delete('/admin/integrations/fooddata')).data,
    onMutate: () => setSaved(null),
    onSuccess: () => {
      setSaved('USDA key removed. Barcode scans use Open Food Facts only.');
      refresh();
    },
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    const k = apiKey.trim();
    save.reset();
    setSaved(null);
    if (!USDA_KEY_SHAPE.test(k)) {
      setError('Paste the whole key from api.data.gov. It is a long mix of letters and numbers.');
      return;
    }
    setError(undefined);
    save.mutate(k);
  }

  return (
    <Card>
      <h3 style={{ marginTop: 0 }}>USDA food data (barcode scans)</h3>
      <div style={{ display: 'grid', gap: 8 }}>
        {saved && <Notice kind="ok">{saved}</Notice>}
        <ErrorLine error={save.error} fallback="Could not save the USDA key." />
        <ErrorLine error={remove.error} fallback="Could not remove the USDA key." />
      </div>
      <div style={{ display: 'grid', gap: 10, marginTop: 8 }}>
        {key ? (
          <p style={{ margin: 0 }}>
            <strong style={{ color: rejected ? 'var(--crave-red)' : 'var(--ok)' }}>
              {rejected ? 'Key not accepted' : 'Key saved'}
            </strong>{' '}
            · ends in <code>{(key.hint ?? '').replace(/^…/, '')}</code> · set{' '}
            {new Date(key.set_at).toLocaleString()}
          </p>
        ) : (
          <p style={{ margin: 0, color: 'var(--ink-soft)' }}>
            No key saved. Barcode scans on the Ingredients page use Open Food Facts only.
          </p>
        )}
        {rejected && integration?.last_error && (
          <Notice kind="error">
            {integration.last_error} Get a new key with the steps below and paste it here.
          </Notice>
        )}
        <p style={{ margin: 0, color: 'var(--ink-soft)' }}>
          USDA keeps the Nutrition Facts that food makers send in, so a scan can find the label of
          the exact product you buy. The key is free and takes about a minute:
        </p>
        <ol style={{ margin: 0, paddingLeft: 22, display: 'grid', gap: 4 }}>
          <li>
            Open{' '}
            <a
              href="https://api.data.gov/signup/"
              target="_blank"
              rel="noreferrer"
              style={{ color: 'var(--ink)', fontWeight: 600, textUnderlineOffset: 3 }}
            >
              api.data.gov/signup
            </a>
            .
          </li>
          <li>Fill in your first name, last name and email, and send the form.</li>
          <li>The key shows on the next page and is also emailed to you. Copy it.</li>
          <li>Paste it below and tap Save key.</li>
        </ol>
        <form onSubmit={submit} style={{ display: 'grid', gap: 4 }}>
          <FormGrid min={FORM_COL}>
            <Field
              label={key ? 'New USDA key' : 'USDA key'}
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              autoComplete="off"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              hint="Stored encrypted. Only its last 4 characters are shown."
              error={error}
              required
            />
            <FormAction>
              <Button type="submit" disabled={save.isPending || !apiKey.trim()}>
                {save.isPending ? 'Saving…' : 'Save key'}
              </Button>
            </FormAction>
          </FormGrid>
        </form>
        {key && (
          <div>
            <Button
              variant="danger"
              onClick={() => {
                if (confirm('Remove the USDA key? Barcode scans will use Open Food Facts only.'))
                  remove.mutate();
              }}
              disabled={remove.isPending}
            >
              Remove key
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
}

/* ---------------------------------- Twilio --------------------------------- */

interface TwilioErrors {
  account_sid?: string;
  auth_token?: string;
  from_number?: string;
}

/** A US number from what people type, as ten digits, or null. The API checks it again. */
function usDigits(typed: string): string | null {
  let d = typed.replace(/\D/g, '');
  if (d.length === 11 && d.startsWith('1')) d = d.slice(1);
  return /^[2-9]\d{2}[2-9]\d{6}$/.test(d) ? d : null;
}
const showPhone = (e164: string) => {
  const d = e164.replace(/^\+1/, '');
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : e164;
};

function validateTwilio(sid: string, token: string, from: string) {
  const errors: TwilioErrors = {};
  if (!/^AC[0-9a-fA-F]{32}$/.test(sid.trim()))
    errors.account_sid = 'The Account SID starts with AC and is 34 characters long.';
  if (!/^[0-9a-fA-F]{32}$/.test(token.trim()))
    errors.auth_token = 'The Auth Token is 32 letters and numbers. Use Show in Twilio to copy it.';
  if (!usDigits(from)) errors.from_number = 'Enter your Twilio number, like (863) 777-4887.';
  return errors;
}

function TwilioCard({ integration }: { integration: Integration | undefined }) {
  const refresh = useRefreshConnections();
  const connected = integration?.status === 'connected';
  const currentSid = str(integration?.config['account_sid']);
  const currentFrom = str(integration?.config['from_number']);
  const token = integration?.secrets.find((s) => s.key_name === 'auth_token');
  const [sid, setSid] = useState(currentSid);
  const [authToken, setAuthToken] = useState('');
  const [from, setFrom] = useState(currentFrom ? showPhone(currentFrom) : '');
  const [errors, setErrors] = useState<TwilioErrors>({});
  const [saved, setSaved] = useState<string | null>(null);
  const [testTo, setTestTo] = useState('');
  const [testError, setTestError] = useState<string | undefined>();

  const save = useMutation({
    mutationFn: async (body: { account_sid: string; auth_token: string; from_number: string }) =>
      (
        await api.put<{ status: string; from_display: string; replies_to_crave: boolean }>(
          '/admin/integrations/twilio',
          body,
        )
      ).data,
    onMutate: () => setSaved(null),
    onSuccess: (r) => {
      setAuthToken('');
      setErrors({});
      setSaved(
        `Twilio connected. Texts come from ${r.from_display}${r.replies_to_crave ? ', and replies arrive in Messages' : ''}.`,
      );
      refresh();
    },
  });
  const sendTest = useMutation({
    mutationFn: async (to: string) =>
      (await api.post<{ ok: boolean }>('/admin/integrations/twilio/test', { to })).data,
    onMutate: () => setSaved(null),
    onSuccess: (_r, to) => setSaved(`Test text sent to ${to}. It should arrive within a minute.`),
  });
  const disconnect = useMutation({
    mutationFn: async () => (await api.delete('/admin/integrations/twilio')).data,
    onMutate: () => setSaved(null),
    onSuccess: () => {
      setSaved('Twilio disconnected. No texts go out until you connect it again.');
      setSid('');
      setFrom('');
      refresh();
    },
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    const next = validateTwilio(sid, authToken, from);
    setErrors(next);
    setSaved(null);
    save.reset();
    if (Object.keys(next).length > 0) return;
    save.mutate({
      account_sid: sid.trim(),
      auth_token: authToken.trim(),
      from_number: from.trim(),
    });
  }

  function submitTest(e: FormEvent) {
    e.preventDefault();
    sendTest.reset();
    const d = usDigits(testTo);
    if (!d) {
      setTestError('Enter a US mobile number, like (863) 555-0100.');
      return;
    }
    setTestError(undefined);
    sendTest.mutate(showPhone(`+1${d}`));
  }

  const form = (
    <form onSubmit={submit} style={{ display: 'grid', gap: 4, marginTop: 12 }}>
      <FormGrid min={FORM_COL}>
        <Field
          label="Account SID"
          placeholder="AC…"
          value={sid}
          onChange={(e) => setSid(e.target.value)}
          autoComplete="off"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          hint="On the Twilio console home page, under Account Info."
          error={errors.account_sid}
          required
        />
        <Field
          label={token ? 'Auth Token (paste it again to save changes)' : 'Auth Token'}
          value={authToken}
          onChange={(e) => setAuthToken(e.target.value)}
          type="password"
          autoComplete="off"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          hint="Next to the Account SID. Stored encrypted; only its last 4 characters are shown."
          error={errors.auth_token}
          required
        />
        <Field
          label="Twilio number"
          placeholder="(863) 777-4887"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          inputMode="tel"
          autoComplete="off"
          hint="The number you bought in Twilio. Texts come from it."
          error={errors.from_number}
          required
        />
      </FormGrid>
      <div>
        <Button type="submit" disabled={save.isPending || !authToken.trim()}>
          {save.isPending
            ? 'Checking with Twilio…'
            : connected
              ? 'Save Twilio settings'
              : 'Connect Twilio'}
        </Button>
      </div>
    </form>
  );

  return (
    <Card>
      <h3 style={{ marginTop: 0 }}>Twilio (text messages)</h3>
      <div style={{ display: 'grid', gap: 8 }}>
        {saved && <Notice kind="ok">{saved}</Notice>}
        <ErrorLine error={save.error} fallback="Could not connect Twilio." />
        <ErrorLine error={disconnect.error} fallback="Could not disconnect Twilio." />
        <ErrorLine error={sendTest.error} fallback="Could not send the test text." />
      </div>
      <div style={{ display: 'grid', gap: 10, marginTop: 8 }}>
        <p style={{ margin: 0, color: 'var(--ink-soft)' }}>
          At checkout, customers can tick “Text me about this order”. They then get a text when the
          order is booked, when it is ready, and if it is canceled. Nobody is texted without ticking
          it, and nothing else is ever sent by text.
        </p>
        {connected ? (
          <>
            <p style={{ margin: 0 }}>
              <strong style={{ color: 'var(--ok)' }}>Connected</strong> · texts come from{' '}
              <strong>{showPhone(currentFrom)}</strong>
              {token && (
                <>
                  {' '}
                  · token ends in <code>{(token.hint ?? '').replace(/^.*…/, '')}</code>
                </>
              )}
            </p>
            <ul style={{ margin: 0, paddingLeft: 20, display: 'grid', gap: 4 }}>
              <li>
                {integration?.config['texts_webhook'] === 'crave' ? (
                  <>
                    Replies from customers arrive in <Link to="/messages">Messages</Link>.
                  </>
                ) : (
                  <span style={{ color: 'var(--crave-red)' }}>
                    Replies are not reaching Crave yet. Save the Twilio settings again to point the
                    number’s incoming texts at Crave.
                  </span>
                )}
              </li>
              <li>
                Calls to this number are left as they are
                {str(integration?.config['voice_host'])
                  ? ` (they go to ${str(integration?.config['voice_host'])})`
                  : ''}
                . Crave never changes them.
              </li>
            </ul>
            {integration?.last_error && <Notice kind="error">{integration.last_error}</Notice>}
            <form onSubmit={submitTest} style={{ marginBottom: -8 }}>
              <FormGrid min={FORM_COL}>
                <Field
                  label="Send a test text to"
                  placeholder="Your mobile number"
                  value={testTo}
                  onChange={(e) => setTestTo(e.target.value)}
                  inputMode="tel"
                  autoComplete="tel"
                  error={testError}
                />
                <FormAction>
                  <Button type="submit" disabled={sendTest.isPending || !testTo.trim()}>
                    {sendTest.isPending ? 'Sending…' : 'Send test text'}
                  </Button>
                </FormAction>
              </FormGrid>
            </form>
            <div>
              <Button
                variant="danger"
                onClick={() => {
                  if (confirm('Disconnect Twilio? No texts go out until you connect it again.'))
                    disconnect.mutate();
                }}
                disabled={disconnect.isPending}
              >
                Disconnect Twilio
              </Button>
            </div>
            <details>
              <summary
                style={{
                  cursor: 'pointer',
                  // Padding, not flex: a flex summary loses its disclosure arrow in Safari.
                  padding: '10px 0',
                }}
              >
                Change the token or number
              </summary>
              {form}
            </details>
          </>
        ) : (
          <>
            <ol style={{ margin: 0, paddingLeft: 22, display: 'grid', gap: 4 }}>
              <li>
                Open{' '}
                <a
                  href="https://console.twilio.com/"
                  target="_blank"
                  rel="noreferrer"
                  style={{ color: 'var(--ink)', fontWeight: 600, textUnderlineOffset: 3 }}
                >
                  console.twilio.com
                </a>{' '}
                and sign in.
              </li>
              <li>
                On the home page, under Account Info, copy the Account SID and the Auth Token.
              </li>
              <li>Paste them below with your Twilio number and tap Connect Twilio.</li>
            </ol>
            {form}
          </>
        )}
        <p style={{ margin: 0, color: 'var(--ink-soft)' }}>
          Carriers only deliver business texts from a number registered for them (A2P 10DLC). If the
          registration on your Twilio account describes a different business, add order
          notifications for Crave Pizza to it. When Twilio asks how customers agree and for your
          terms, give it <strong>order.cravepizzafl.com/texts</strong>.
        </p>
      </div>
    </Card>
  );
}

/* ---------------------------------- Resend --------------------------------- */

interface ResendErrors {
  api_key?: string;
  from_address?: string;
}

const EMAIL = /^[^\s@<>]+@[^\s@<>]+\.[^\s@<>]+$/;

function validateResend(apiKey: string, fromAddress: string) {
  const errors: ResendErrors = {};
  const key = apiKey.trim();
  const from = fromAddress.trim();
  if (!key.startsWith('re_') || key.length < 10 || key.length > 200)
    errors.api_key = 'Paste a Resend API key that starts with re_.';
  const named = /^(.*?)\s*<([^<>]+)>$/.exec(from);
  const address = (named ? named[2] : from)?.trim() ?? '';
  const nameOk = !named || Boolean(named[1]?.trim());
  if (!from) errors.from_address = 'Enter the address order emails come from.';
  else if (!EMAIL.test(address) || !nameOk || from.length > 200)
    errors.from_address = 'Use a name and address like Crave Pizza <orders@cravepizzafl.com>.';
  return errors;
}

function ResendCard({ integration: resend }: { integration: Integration | undefined }) {
  const refresh = useRefreshConnections();
  const currentFrom = str(resend?.config['from_address']);
  const [apiKey, setApiKey] = useState('');
  const [fromAddress, setFromAddress] = useState(currentFrom);
  const [errors, setErrors] = useState<ResendErrors>({});
  const [saved, setSaved] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async (body: { api_key: string; from_address: string }) =>
      (await api.put<{ status: string }>('/admin/integrations/resend', body)).data,
    onMutate: () => setSaved(null),
    onSuccess: (_r, body) => {
      setApiKey('');
      setErrors({});
      setSaved(`Email connected. Order emails send from ${body.from_address}.`);
      refresh();
    },
  });
  const [testTo, setTestTo] = useState('');
  const sendTest = useMutation({
    mutationFn: async (to: string) =>
      (
        await api.post<{ to: string; provider_message_id: string }>(
          '/admin/integrations/resend/test',
          to ? { to } : {},
        )
      ).data,
    onMutate: () => setSaved(null),
    onSuccess: (r) =>
      setSaved(
        `Test email sent to ${r.to}. It should arrive within a minute. Check Junk if you don't see it.`,
      ),
  });
  const disconnect = useMutation({
    mutationFn: async () => (await api.delete('/admin/integrations/resend')).data,
    onMutate: () => setSaved(null),
    onSuccess: () => {
      setSaved('Email disconnected.');
      setFromAddress('');
      refresh();
    },
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    const next = validateResend(apiKey, fromAddress);
    setErrors(next);
    setSaved(null);
    save.reset();
    if (Object.keys(next).length > 0) return;
    save.mutate({ api_key: apiKey.trim(), from_address: fromAddress.trim() });
  }

  const connected = resend?.status === 'connected';
  const form = (
    <ResendForm
      apiKey={apiKey}
      setApiKey={setApiKey}
      fromAddress={fromAddress}
      setFromAddress={setFromAddress}
      errors={errors}
      pending={save.isPending}
      submitLabel={connected ? 'Save email settings' : 'Connect email'}
      onSubmit={submit}
    />
  );

  return (
    <Card>
      <h3 style={{ marginTop: 0 }}>Resend (email)</h3>
      <div style={{ display: 'grid', gap: 8 }}>
        {saved && <Notice kind="ok">{saved}</Notice>}
        <ErrorLine error={save.error} fallback="Could not save the email settings." />
        <ErrorLine error={disconnect.error} fallback="Could not disconnect email." />
        <ErrorLine error={sendTest.error} fallback="Could not send the test email." />
      </div>
      {connected ? (
        <div style={{ display: 'grid', gap: 8, marginTop: 8 }}>
          <p style={{ margin: 0 }}>
            <strong style={{ color: 'var(--ok)' }}>Connected</strong> · sending from{' '}
            <code>{currentFrom || 'no From address set'}</code>
          </p>
          {resend.secrets.length > 0 && (
            <ul style={{ margin: 0 }}>
              {resend.secrets.map((s) => (
                <li key={s.key_name}>
                  API key: <code>{s.hint}</code> · set {new Date(s.set_at).toLocaleString()}
                </li>
              ))}
            </ul>
          )}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              sendTest.mutate(testTo.trim());
            }}
            style={{ marginTop: 4, marginBottom: -8 }}
          >
            <FormGrid min={FORM_COL}>
              <Field
                label="Send a test to"
                type="email"
                placeholder="Leave blank to send it to yourself"
                value={testTo}
                onChange={(e) => setTestTo(e.target.value)}
              />
              <FormAction>
                <Button type="submit" disabled={sendTest.isPending}>
                  {sendTest.isPending ? 'Sending…' : 'Send test email'}
                </Button>
              </FormAction>
            </FormGrid>
          </form>
          <div>
            <Button
              variant="danger"
              onClick={() => {
                if (confirm('Disconnect email? Order emails stop until you connect it again.'))
                  disconnect.mutate();
              }}
              disabled={disconnect.isPending}
            >
              Disconnect email
            </Button>
          </div>
          <details>
            <summary style={{ cursor: 'pointer' }}>Replace the key or From address</summary>
            {form}
          </details>
        </div>
      ) : (
        <>
          {resend?.status === 'error' && resend.last_error && !save.error && (
            <p style={{ color: 'var(--crave-red)' }}>Last attempt failed: {resend.last_error}</p>
          )}
          <p style={{ color: 'var(--ink-soft)' }}>
            Order confirmations, ready notices and receipts are sent through Resend. Until email is
            connected, no customer emails go out.
          </p>
          {form}
        </>
      )}
    </Card>
  );
}

interface ResendFormProps {
  apiKey: string;
  setApiKey: (v: string) => void;
  fromAddress: string;
  setFromAddress: (v: string) => void;
  errors: ResendErrors;
  pending: boolean;
  submitLabel: string;
  onSubmit: (e: FormEvent) => void;
}

function ResendForm(p: ResendFormProps) {
  return (
    <form onSubmit={p.onSubmit} style={{ display: 'grid', gap: 4, marginTop: 12 }}>
      <FormGrid min={FORM_COL}>
        <Field
          label="Resend API key"
          placeholder="re_…"
          value={p.apiKey}
          onChange={(e) => p.setApiKey(e.target.value)}
          autoComplete="off"
          spellCheck={false}
          hint="Create one in Resend under API Keys, with sending access."
          error={p.errors.api_key}
          required
        />
        <Field
          label="From address"
          placeholder="Crave Pizza <orders@cravepizzafl.com>"
          value={p.fromAddress}
          onChange={(e) => p.setFromAddress(e.target.value)}
          autoComplete="off"
          spellCheck={false}
          hint="The domain must be verified in Resend."
          error={p.errors.from_address}
          required
        />
      </FormGrid>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <Button type="submit" disabled={p.pending || !p.apiKey.trim()}>
          {p.pending ? 'Saving…' : p.submitLabel}
        </Button>
      </div>
    </form>
  );
}
